import React, {createRef, useEffect, useState} from "react";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import FirstPage from "@material-ui/icons/FirstPage";
import NavigateBefore from "@material-ui/icons/NavigateBefore";
import NavigateNext from "@material-ui/icons/NavigateNext";
import LastPage from "@material-ui/icons/LastPage";
import useExpanderTable from "../expander-table/hooks";
import {useMediaQuery} from "@material-ui/core";

const ExpanderTablePager = ({onVisibilityChange}) => {
    const [state, setState] = useExpanderTable();
    const [isVisible, setIsVisible] = useState(false)
    const ref = createRef();

    // This should run after each render
    useEffect(() => {
        if (onVisibilityChange && ref.current) {

            // Get the current visibility
            const top = ref.current.getBoundingClientRect().top;
            const innerHeight = window.innerHeight;
            const visibleNow = ((top >= 0) && (top <= innerHeight));
            const viewPortOffset = top < 0 ? top : top < innerHeight ? 0 : top - innerHeight

            // If it has changed from last time, update the state and call the callback function
            if (isVisible !== visibleNow) {
                setIsVisible(visibleNow);
                onVisibilityChange(visibleNow, viewPortOffset);
            }
        }
    }, [onVisibilityChange, ref, isVisible])

    if (!state) {
        console.warn("ExpanderTableContent must be a descendant of ExpanderTable")
        return null;
    }

    const nextPage = () => {
        if (state.page < state.pageCount && !state.isFetching) {
            setState({...state, page: state.page+1})
        }
    }

    const previousPage = () => {
        if (state.page > 0 && !state.isFetching) {
            setState({...state, page: state.page-1})
        }
    }

    const firstPage = () => {
        setState({...state, page: 0})
    }

    const lastPage = () => {
        setState({...state, page: state.pageCount})
    }

    const handlePageLength = (event) => {
        const pageLength = event.target.value

        // Calculate the new pageCount
        const pageCount = Math.ceil(state.rowsFiltered/pageLength) - 1;

        // Select the page with the current record
        const currentTop = state.page * state.pageLength + 1
        const page = Math.floor(currentTop / pageLength);

        setState({...state, pageLength, pageCount, page})
    }

    return (
        <div ref={ref} className="flex flex-grow items-center justify-between px-4 bg-white shadow rounded-b h-12">
            <Select value={state.pageLength} onChange={handlePageLength} disableUnderline>
                <MenuItem key={5} value={5}><span className="text-sm">5 rows</span></MenuItem>
                <MenuItem key={10} value={10}><span className="text-sm">10 rows</span></MenuItem>
                <MenuItem key={20} value={20}><span className="text-sm">20 rows</span></MenuItem>
                <MenuItem key={30} value={30}><span className="text-sm">30 rows</span></MenuItem>
                <MenuItem key={40} value={40}><span className="text-sm">40 rows</span></MenuItem>
                <MenuItem key={50} value={50}><span className="text-sm">50 rows</span></MenuItem>
            </Select>

            <PagerControls
                state={state}
                onClickNext={nextPage}
                onClickPrevious={previousPage}
                onClickFirst={firstPage}
                onClickLast={lastPage}
            />
        </div>

    )
}

const PagerControls = ({state, onClickNext, onClickPrevious, onClickFirst, onClickLast,  widthOffset=0}) => {

    const is375 = useMediaQuery('(max-width:374px)');
    const is460 = useMediaQuery('(max-width:460px)');
    const is560 = useMediaQuery('(max-width:560px)');
    const isWide  = useMediaQuery('(min-width:768px)');

    if (state.rowsFiltered === 0) {
        return null
    }
    const first = state.page * state.pageLength + 1;
    const last = Math.min(first + state.pageLength - 1, state.rowsFiltered, state.rowsTotal);

    if (is375) {
        return (
            <div>
                <IconButton onClick={onClickPrevious}><NavigateBefore/></IconButton>
                {state.page + 1} / {state.pageCount + 1}
                <IconButton onClick={onClickNext}><NavigateNext/></IconButton>
            </div>
        )

    } else if (is460) {
        return (
            <div>
                <IconButton onClick={onClickFirst}><FirstPage/></IconButton>
                <IconButton onClick={onClickPrevious}><NavigateBefore/></IconButton>
                {state.page + 1} / {state.pageCount + 1}
                <IconButton onClick={onClickNext}><NavigateNext/></IconButton>
                <IconButton onClick={onClickLast}><LastPage/></IconButton>
            </div>
        )

    } else if (is560) {
        return (
            <div className="flex flex-row items-center text-right">
                Page {state.page + 1} of {state.pageCount + 1}
                <IconButton onClick={onClickFirst}><FirstPage/></IconButton>
                <IconButton onClick={onClickPrevious}><NavigateBefore/></IconButton>
                <IconButton onClick={onClickNext}><NavigateNext/></IconButton>
                <IconButton onClick={onClickLast}><LastPage/></IconButton>
            </div>
        )
    } else {
        let description;
        // Display based on filtered / unfiltered
        if (state.rowsFiltered === state.rowsTotal) {
            // No filtered records
            if (isWide) {
                description = `Showing ${first} to ${last} of ${state.rowsTotal} entries`
            } else {
                description = `${first} to ${last} of ${state.rowsTotal}`
            }

        } else {
            // Filtered records
            if (isWide) {
                description = `Showing ${first} to ${last} of ${state.rowsFiltered} entries (filtered from ${state.rowsTotal} entries)`
            } else {
                description = `Showing ${first} to ${last} of ${state.rowsFiltered}/${state.rowsTotal}`
            }

        }
        return (
            <div className="flex flex-row items-center text-right">
                {description}
                <div className="flex-shrink-0">
                    <IconButton onClick={onClickFirst}><FirstPage/></IconButton>
                    <IconButton onClick={onClickPrevious}><NavigateBefore/></IconButton>
                    <IconButton onClick={onClickNext}><NavigateNext/></IconButton>
                    <IconButton onClick={onClickLast}><LastPage/></IconButton>
                </div>
            </div>
        )
    }
}


export default ExpanderTablePager;